/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, Text } from '@fluentui/react/lib/Text';
import { Property } from 'csstype';
import * as React from 'react';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { getStylist } from 'src/core/themes/luisStylist';

export type TitledSectionProps = React.PropsWithChildren<{
    /**
     * The title to add to the section.
     */
    title: string;

    /**
     * An optional description to display
     * in a callout
     */
    description?: string;
}>;

const { styleComponent, getClassName } = getStylist('TitledSection');
const SectionContainer = styleComponent<IStackProps>(Stack)('TitledSectionContainer', theme => ({
    marginBottom: theme.containerMarginBottom
}));
const SectionHeader = styleComponent<ITextProps>(Text)('TitledSectionHeader', theme => ({
    fontWeight: theme.titleFontWeight as Property.FontWeight
}));
const calloutAdjust = getClassName('CalloutButtonAdjust', theme => ({ position: 'relative', top: theme.calloutTopAdjust }));

/**
 * A simple component to standardize the way we display
 * sections of controls/data with titles.
 *
 * @param props The input to the section.
 */
export const TitledSection = (props: TitledSectionProps) => {
    const { title, description } = props;

    return (
        <SectionContainer tokens={{ childrenGap: 24 }}>
            <SectionHeader as="h2" variant="xLarge">
                {title}
                {description && <CalloutButton containerClassName={calloutAdjust} content={description} />}
            </SectionHeader>

            <section>{props.children}</section>
        </SectionContainer>
    );
};
